import { useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiEdit2, FiTrash2, FiCopy, FiPlus, FiX, FiChevronLeft, FiChevronRight, FiShare2 } from 'react-icons/fi';
import ShareDialog from '../components/ShareDialog';
import DashboardLayout from '../components/DashboardLayout.js';
import { fetchEvents, createEvent, deleteEvent, updateEvent, searchTradeShows } from '../apiService';
import './Events.css';

const EMPTY_FORM = {
  eventName: '',
  venue: '',
  eventDate: '',
  endDate: '',
  exhibitStartDate: '',
  exhibitEndDate: '',
  website: '',
  market: '',
  contactTitles: '',
  numberOfAttendees: '',
  callForPresentationDate: '',
  sponsorshipOpportunities: '',
  otherSpeakingOpportunities: '',
  exhibitorContactInfo: '',
  boothSize: '',
  exhibitorBenefits: '',
  status: 'Drafts',
  budget: '',
};

function Events() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [events, setEvents] = useState([]);
  const [shareEvent, setShareEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [formError, setFormError] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // The Playbook links here with ?edit=<id> so a show's details can be
  // changed without duplicating this form in two places.
  const [searchParams, setSearchParams] = useSearchParams();
  const editIdFromUrl = searchParams.get('edit');
  const openedFromUrlRef = useRef(false);

  useEffect(() => {
    if (openedFromUrlRef.current || !editIdFromUrl || events.length === 0) return;
    const match = events.find(e => e.id === editIdFromUrl);
    if (match) {
      openedFromUrlRef.current = true;
      handleEditClick(match);
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, editIdFromUrl]);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    if (!user.userId) return;
    setLoading(true);
    const result = await fetchEvents(user.userId);
    if (result.success) {
      setEvents(result.data);
      // The sidebar keeps its own copy of the event list, so tell it to
      // refresh too — otherwise a newly published show only appears there
      // after navigating away and back.
      window.dispatchEvent(new Event('zibit:events-changed'));
    }
    setLoading(false);
  }

  function buildPayload() {
    return {
      eventName: formData.eventName,
      venue: formData.venue,
      eventDate: formData.eventDate || null,
      endDate: formData.endDate || null,
      exhibitStartDate: formData.exhibitStartDate || null,
      exhibitEndDate: formData.exhibitEndDate || null,
      website: formData.website || null,
      market: formData.market || null,
      contactTitles: formData.contactTitles || null,
      numberOfAttendees: formData.numberOfAttendees || null,
      callForPresentationDate: formData.callForPresentationDate || null,
      sponsorshipOpportunities: formData.sponsorshipOpportunities || null,
      otherSpeakingOpportunities: formData.otherSpeakingOpportunities || null,
      exhibitorContactInfo: formData.exhibitorContactInfo || null,
      boothSize: formData.boothSize || null,
      exhibitorBenefits: formData.exhibitorBenefits || null,
      status: formData.status.toLowerCase(),
      budget: formData.budget || null,
    };
  }

  async function handleSubmitEvent(e) {
    e.preventDefault();
    setFormError('');

    if (!formData.eventName || !formData.venue) {
      setFormError('Event name and location are required.');
      return;
    }

    let result;
    if (editingEvent) {
      result = await updateEvent(editingEvent.id, buildPayload());
    } else {
      result = await createEvent({ userId: user.userId, ...buildPayload() });
    }

    if (result.success) {
      closeForm();
      loadEvents();
    } else {
      setFormError(result.error || 'Failed to save event.');
    }
  }

  function closeForm() {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({ ...EMPTY_FORM });
    setSuggestions([]);
    setShowSuggestions(false);
  }

  function handleInputChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  async function handleEventNameChange(e) {
    const value = e.target.value;
    setFormData({ ...formData, eventName: value });
    if (value.length >= 2) {
      const result = await searchTradeShows(value);
      if (result.success && result.data.response) {
        setSuggestions(result.data.response.docs || []);
        setShowSuggestions(true);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }

  function handleSuggestionClick(show) {
    function convertDate(dateStr) {
      if (!dateStr) return '';
      const months = {
        'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
        'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
        'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
      };
      const parts = dateStr.split('/');
      if (parts.length !== 3) return '';
      return `${parts[2]}-${months[parts[0]]}-${parts[1]}`;
    }

    setFormData({
      ...formData,
      eventName: show.name[0],
      venue: show.location ? show.location[0] : formData.venue,
      eventDate: show.start_date ? convertDate(show.start_date[0]) : formData.eventDate,
      endDate: show.end_date ? convertDate(show.end_date[0]) : formData.endDate,
      numberOfAttendees: show.attendees ? show.attendees[0].toString() : formData.numberOfAttendees,
    });
    setSuggestions([]);
    setShowSuggestions(false);
  }

  async function handleDeleteEvent(eventId) {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    const result = await deleteEvent(eventId);
    if (result.success) {
      loadEvents();
    }
  }

  function handleEditClick(event) {
    setEditingEvent(event);
    setFormData({
      eventName: event.eventName || '',
      venue: event.venue || '',
      eventDate: event.eventDate || '',
      endDate: event.endDate || '',
      exhibitStartDate: event.exhibitStartDate || '',
      exhibitEndDate: event.exhibitEndDate || '',
      website: event.website || '',
      market: event.market || '',
      contactTitles: event.contactTitles || '',
      numberOfAttendees: event.numberOfAttendees != null ? String(event.numberOfAttendees) : '',
      callForPresentationDate: event.callForPresentationDate || '',
      sponsorshipOpportunities: event.sponsorshipOpportunities || '',
      otherSpeakingOpportunities: event.otherSpeakingOpportunities || '',
      exhibitorContactInfo: event.exhibitorContactInfo || '',
      boothSize: event.boothSize || '',
      exhibitorBenefits: event.exhibitorBenefits || '',
      status: event.status || 'Drafts',
      budget: event.budget || '',
    });
    setShowForm(true);
  }

  const filteredEvents = events.filter(event =>
    event.eventName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (event.venue && event.venue.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredEvents.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedEvents = filteredEvents.slice(startIndex, startIndex + rowsPerPage);

  function getStatusClass(status) {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s === 'active' || s === 'published') return 'status-published';
    if (s === 'completed') return 'status-completed';
    return 'status-draft';
  }

  function getStatusLabel(status) {
    if (!status) return 'Drafts';
    const s = status.toLowerCase();
    if (s === 'active') return 'Published';
    if (s === 'completed') return 'Completed';
    if (s === 'planning') return 'Drafts';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  const textareaStyle = { width: '100%', minHeight: '56px', resize: 'vertical', fontFamily: 'inherit', fontSize: '14px', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' };

  return (
    <DashboardLayout>
      <h1 className="page-title">Events</h1>

      <div className="events-actions">
        <button className="create-btn" onClick={() => setShowForm(true)}>
          <FiPlus size={18} />
          Create New Event
        </button>
        <div className="search-box">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
          <FiSearch className="search-icon" size={16} />
        </div>
      </div>

      {showForm && (
        <div className="event-form-overlay">
          <div className="event-form-card" style={{ maxHeight: '88vh', overflowY: 'auto' }}>
            <div className="form-header">
              <h2>{editingEvent ? 'Edit Event' : 'Create New Event'}</h2>
              <button className="close-btn" onClick={closeForm}>
                <FiX size={20} />
              </button>
            </div>
            {formError && <p className="form-error">{formError}</p>}
            <form onSubmit={handleSubmitEvent}>
              <div className="form-group">
                <label>Event Name</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleEventNameChange}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="Enter event name"
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {suggestions.map((show, index) => (
                        <div
                          key={index}
                          onMouseDown={() => handleSuggestionClick(show)}
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '14px'
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <div style={{ fontWeight: '500' }}>{show.name[0]}</div>
                          {show.location && <div style={{ fontSize: '12px', color: '#888' }}>{show.location[0]}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Location</label>
                  <input type="text" name="venue" value={formData.venue} onChange={handleInputChange} placeholder="City, State" />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input type="text" name="website" value={formData.website} onChange={handleInputChange} placeholder="https://" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" name="eventDate" value={formData.eventDate} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" name="endDate" value={formData.endDate} onChange={handleInputChange} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Exhibit Start Date</label>
                  <input type="date" name="exhibitStartDate" value={formData.exhibitStartDate} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Exhibit End Date</label>
                  <input type="date" name="exhibitEndDate" value={formData.exhibitEndDate} onChange={handleInputChange} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Market</label>
                  <input type="text" name="market" value={formData.market} onChange={handleInputChange} placeholder="e.g. Healthcare, Senior Living" />
                </div>
                <div className="form-group">
                  <label>Contact Titles</label>
                  <input type="text" name="contactTitles" value={formData.contactTitles} onChange={handleInputChange} placeholder="Titles of attendees you want to meet" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Number of Attendees</label>
                  <input type="number" name="numberOfAttendees" value={formData.numberOfAttendees} onChange={handleInputChange} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Call for Presentation Date</label>
                  <input type="date" name="callForPresentationDate" value={formData.callForPresentationDate} onChange={handleInputChange} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Booth Size</label>
                  <input type="text" name="boothSize" value={formData.boothSize} onChange={handleInputChange} placeholder="e.g. 10x10" />
                </div>
                <div className="form-group">
                  <label>Budget (expected)</label>
                  <input type="number" name="budget" value={formData.budget} onChange={handleInputChange} placeholder="0.00" />
                </div>
              </div>
              <div className="form-group">
                <label>Sponsorship Opportunities</label>
                <textarea name="sponsorshipOpportunities" value={formData.sponsorshipOpportunities} onChange={handleInputChange} style={textareaStyle} />
              </div>
              <div className="form-group">
                <label>Other Speaking Opportunities</label>
                <textarea name="otherSpeakingOpportunities" value={formData.otherSpeakingOpportunities} onChange={handleInputChange} style={textareaStyle} />
              </div>
              <div className="form-group">
                <label>Exhibitor Contact Info</label>
                <textarea name="exhibitorContactInfo" value={formData.exhibitorContactInfo} onChange={handleInputChange} style={textareaStyle} />
              </div>
              <div className="form-group">
                <label>Exhibitor Benefits</label>
                <textarea name="exhibitorBenefits" value={formData.exhibitorBenefits} onChange={handleInputChange} style={textareaStyle} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="Drafts">Drafts</option>
                  <option value="active">Published</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeForm}>Cancel</button>
                <button type="submit" className="submit-btn">{editingEvent ? 'Save Changes' : 'Create Event'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="events-table-card">
        <table className="events-table">
          <thead>
            <tr>
              <th className="th-highlight">Events</th>
              <th>Location</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="table-loading">Loading...</td></tr>
            ) : paginatedEvents.length > 0 ? (
              paginatedEvents.map((event) => (
                <tr
                  key={event.id}
                  className="event-row-clickable"
                  onClick={() => handleEditClick(event)}
                >
                  <td className="event-name">{event.eventName}</td>
                  <td>{event.venue || '—'}</td>
                  <td>{event.eventDate || '—'}</td>
                  <td>{event.endDate || '—'}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(event.status)}`}>
                      {getStatusLabel(event.status)}
                    </span>
                  </td>
                  <td className="actions-cell" onClick={e => e.stopPropagation()}>
                    <button className="action-btn" title="Copy"><FiCopy size={16} /></button>
                    <button className="action-btn" title="Edit" onClick={() => handleEditClick(event)}><FiEdit2 size={16} /></button>
                    <button className="action-btn" title="Share" onClick={() => setShareEvent(event)}><FiShare2 size={16} /></button>
                    <button className="action-btn delete" title="Delete" onClick={() => handleDeleteEvent(event.id)}><FiTrash2 size={16} /></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" className="table-empty">No events found.</td></tr>
            )}
          </tbody>
        </table>

        <div className="table-footer">
          <div className="rows-per-page">
            <span>Rows per page:</span>
            <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
          </div>
          <div className="pagination">
            <span>{startIndex + 1} - {Math.min(startIndex + rowsPerPage, filteredEvents.length)} of {filteredEvents.length}</span>
            <button className="page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
              <FiChevronLeft size={16} />
            </button>
            <button className="page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0}>
              <FiChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
      {shareEvent && (
        <ShareDialog
          event={shareEvent}
          user={user}
          onClose={() => setShareEvent(null)}
        />
      )}

    </DashboardLayout>
  );
}

export default Events;
